import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Moon, Sun } from 'lucide-react'
import { Button } from '../components/Button'

/** Thème clair par défaut ; le sombre se choisit avec le bouton (mémorisé dans le navigateur). */
export type ThemePreference = 'light' | 'dark'
export type ResolvedTheme = ThemePreference

export const THEME_STORAGE_KEY = 'concours.theme'

interface ThemeApi {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
}

const ThemeContext = createContext<ThemeApi | null>(null)

function readPreference(): ThemePreference {
  try {
    // Toute autre valeur (dont l'ancien « system ») revient au thème clair.
    if (localStorage.getItem(THEME_STORAGE_KEY) === 'dark') return 'dark'
  } catch {
    /* stockage indisponible (navigation privée) */
  }
  return 'light'
}

/**
 * Thème clair / sombre (ADR-015). Clair par défaut, quel que soit le réglage du
 * système ; applique `data-theme` sur <html>. Le choix est mémorisé dans le
 * navigateur (préférence d'affichage uniquement).
 */
export function ThemeProvider({
  children,
  fixed,
}: {
  children: ReactNode
  /** Thème imposé, sans choix possible (portail admin : toujours clair). */
  fixed?: ThemePreference
}) {
  const [chosen, setPreferenceState] = useState<ThemePreference>(() => fixed ?? readPreference())
  const preference = fixed ?? chosen

  useEffect(() => {
    document.documentElement.dataset.theme = preference
  }, [preference])

  const setPreference = useCallback(
    (next: ThemePreference) => {
      if (fixed) return
      setPreferenceState(next)
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next)
      } catch {
        /* ignoré */
      }
    },
    [fixed]
  )

  const api = useMemo(
    () => ({ preference, resolved: preference, setPreference }),
    [preference, setPreference]
  )
  return <ThemeContext.Provider value={api}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeApi {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme doit être utilisé dans <ThemeProvider>.')
  return context
}

/** Bouton clair ↔ sombre : l'icône montre le thème qu'on obtiendra en cliquant. */
export function ThemeToggle() {
  const { preference, setPreference } = useTheme()
  const next: ThemePreference = preference === 'dark' ? 'light' : 'dark'
  const label = next === 'dark' ? 'Passer au thème sombre' : 'Passer au thème clair'
  const Icon = next === 'dark' ? Moon : Sun
  return (
    <Button
      variant="ghost"
      iconOnly
      icon={<Icon />}
      aria-label={label}
      aria-pressed={preference === 'dark'}
      title={label}
      onClick={() => setPreference(next)}
    />
  )
}

/**
 * Script à placer dans <head> (index.html) : applique le thème AVANT le premier
 * rendu (pas de flash clair pour qui a choisi le sombre). Exporté pour être testé.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{document.documentElement.dataset.theme=localStorage.getItem('${THEME_STORAGE_KEY}')==='dark'?'dark':'light'}catch(e){document.documentElement.dataset.theme='light'}})()`

/** Variante pour un portail toujours clair (admin) : aucun choix lu ni mémorisé. */
export const THEME_BOOT_LIGHT_SCRIPT = `(function(){document.documentElement.dataset.theme='light'})()`
